import { convertMeasure, MachineTechnology } from '@e2m-customer-portal/shared';
import { DowntimeButton } from '@e2m-customer-portal/ui';
import { Button, IconButton } from '@mui/material';
import { BatteryIcon, CollapseIcon, SettingsIcon, SolarPowerIcon } from 'assets/icons';
import clsx from 'clsx';
import { DeviceWithInstalledPower } from 'domains/forecast/control-energy-forecast/ControlEnergyForecast';
import { OptimizationGroupParametersType } from 'domains/optimization-group/types';
import { BatterySidePanel } from 'domains/optimization/BatterySidePanel';
import { PVSidePanel } from 'domains/optimization/PVSidePanel';
import { ForecastProperty, ForecastTarget, MarketLocation } from 'generated/graphql';
import { ReactElement, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';

interface DeviceHeaderProps {
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  currentForecastProperty?: ForecastProperty;
  technology: MachineTechnology;
  id?: string | null;
  children?: ReactNode;
  optimizationGroupData?: OptimizationGroupParametersType;
}

export function DeviceHeader({
  marketLocation,
  device,
  currentForecastProperty,
  technology,
  children,
  id,
  optimizationGroupData,
}: Readonly<DeviceHeaderProps>): ReactElement<DeviceHeaderProps> {
  const [t] = useTranslation();
  const [expanded, toggleExpanded] = useToggle(true);
  const [isBatteryPanelOpen, setIsBatteryPanelOpen] = useState(false);
  const [isPVPanelOpen, setIsPVPanelOpen] = useState(false);

  const installedPower = device.installedPower && convertMeasure(device.installedPower, 'kW');

  const isInnoTender =
    optimizationGroupData?.asset_type && ['InnoTender', 'InnoTenderGE'].includes(optimizationGroupData?.asset_type);

  const batteryData = isInnoTender
    ? optimizationGroupData?.battery && ['', optimizationGroupData.battery]
    : optimizationGroupData?.batteries &&
      Object.entries(optimizationGroupData.batteries).find(
        ([_identifier, battery]) => `${battery.device_number}` === device.internalId,
      );

  const pvData = isInnoTender
    ? ['', optimizationGroupData.pv]
    : optimizationGroupData?.pv &&
      Object.entries(optimizationGroupData.pv).find(([_identifier, pv]) => `${pv.device_number}` === device.internalId);

  return (
    <div className="w-full flex flex-col group/device" data-testid={`header-${device.name}`}>
      <div
        className={clsx(
          'pl-4 py-4 flex flex-col md:flex-row items-center justify-between gap-2 border-solid border-0 border-t border-divider',
          {
            'group-last-of-type/device:border-b': !expanded,
          },
        )}
      >
        <div className="flex items-center gap-2">
          <IconButton onClick={toggleExpanded}>
            <CollapseIcon
              className={clsx('text-black flex justify-center items-center transition-transform', {
                '-scale-y-100': !expanded,
              })}
            />
          </IconButton>
          {technology === MachineTechnology.SOLAR ? (
            <SolarPowerIcon title="solar-power-icon" />
          ) : (
            <BatteryIcon title="battery-power-icon" />
          )}
          <div className="font-bold text-base leading-4">{device.externalName || device.name}</div>
          <div className="flex w-max items-center ml-4 border-0 border-l border-divider border-solid">
            {installedPower && (
              <div className="ml-2 flex flex-col whitespace-nowrap">
                <span className="text-xs font-semibold text-grey-300">
                  {t('DEVICE_TABLE_HEADER_INSTALLED_POWER_LABEL')}
                </span>
                <div className="sm:text-md font-bold">
                  <div className="relative flex items-center">
                    {t('DEVICE_TABLE_HEADER_INSTALLED_POWER', { value: installedPower.value })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mr-3 flex gap-2">
          {technology === MachineTechnology.SOLAR && pvData && (
            <Button
              className="text-xs md:text-md"
              startIcon={<SettingsIcon />}
              onClick={(event) => {
                setIsPVPanelOpen(true);
                event.stopPropagation();
              }}
              data-testid="OptimizationParametersPVButton"
            >
              {t('HEADER_MENU_OPTIMIZATION_GROUPS')}
            </Button>
          )}
          {technology === MachineTechnology.BATTERY && batteryData && (
            <Button
              startIcon={<SettingsIcon />}
              onClick={(event) => {
                setIsBatteryPanelOpen(true);
                event.stopPropagation();
              }}
              data-testid="OptimizationParametersBatteryButton"
            >
              {t('HEADER_MENU_OPTIMIZATION_GROUPS')}
            </Button>
          )}
          <DowntimeButton
            marketLocation={marketLocation}
            device={device}
            disabled={
              currentForecastProperty?.target === ForecastTarget.ESDB || !marketLocation.permissions.editForecast
            }
            currentForecastProperty={currentForecastProperty}
          />
        </div>
      </div>
      {expanded && children && <div className="border-solid border-0 border-t border-divider">{children}</div>}
      {optimizationGroupData && batteryData && id && (
        <BatterySidePanel
          data={batteryData[1]}
          identifier={batteryData[0] as string}
          id={id}
          open={isBatteryPanelOpen}
          onClose={() => setIsBatteryPanelOpen(false)}
          assetType={optimizationGroupData.asset_type}
        />
      )}
      {optimizationGroupData && pvData && id && (
        <PVSidePanel
          data={pvData[1]}
          identifier={pvData[0] as string}
          id={id}
          open={isPVPanelOpen}
          onClose={() => setIsPVPanelOpen(false)}
          assetType={optimizationGroupData.asset_type}
        />
      )}
    </div>
  );
}
